import { useRef } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
  getDefaultValuesForForm,
} from '@product-framework/index';
import {
  Breadcrumb,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { useNavigate } from 'react-router-dom';
import { basicSchema } from '../../schemas';
import validationSchema from './validations';

const FORM_ID = 'MAINTENANCE_LABOR_MATRIX_CREATE_FORM';

function LaborMatrixCreate() {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const store = useStore();
  const storeName = 'labormatrixFormStore';

  const postApiUrl = `${getApiUrl()}/sxcl/v1/labor-matrices`;

  const onSubmit = async data => {
    const response = await post(postApiUrl, { ...data });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'LABOR_MATRIX_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'Labor Matrix was saved successfully',
        }),
      );

      return {
        nextValues: data,
        errors: null,
        callback: () => navigate(`/labormatrices/edit/${response?.data?.id}`),
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'LABOR_MATRICES',
        defaultMessage: 'Labor Matrices',
      }),
      icon: IconLibrary.LaborMatrices,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/labormatrices');
      },
    },
    {
      name: formatMessage({
        id: 'CREATE_LABOR_MATRIX',
        defaultMessage: 'Create Labor Matrix',
      }),
      icon: IconLibrary.LaborMatrix,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'LIST_LABOR_MATRICES',
        defaultMessage: 'List Labor Matrices',
      }),
      Icon: IconLibrary.LaborMatrices,
      variant: 'outlined',
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/labormatrices');
      },
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_PAGES',
    },
  ];

  const defaultInitialValues = {
    ...getDefaultValuesForForm({
      schema: basicSchema,
    }),
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'CREATE_LABOR_MATRIX',
          defaultMessage: 'Create Labor Matrix',
        })}>
        <Box>
          <Form
            submitWithReadOnlyFields
            permittedByDefault
            formId={FORM_ID}
            ref={formRef}
            onSubmit={onSubmit}
            schema={basicSchema}
            validationSchema={validationSchema(formatMessage, getApiUrl, get)}
            defaultValues={defaultInitialValues}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'SAVE_LABOR_MATRIX',
                  defaultMessage: 'Save Labor Matrix',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => formRef.current?.reset({})}>
                {formatMessage({
                  id: 'RESET',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </Box>
          </Form>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(LaborMatrixCreate);
